import { z } from 'zod';

import { zodBlankableInt } from '@/utils/zodUtils';

export enum TierOveragePolicy {
  BLOCK = 'block',
  ALLOW_BILLED = 'allow_billed',
}

export const TierCapacityPolicyFieldsSchema = z.object({
  committed_seats: zodBlankableInt({ min: 0 }),
  overage_policy: z.nativeEnum(TierOveragePolicy).optional(),
  overage_cap: zodBlankableInt({ min: 1 }),
});

export interface TierCapacityPolicy {
  seatCount: number;
  committedSeats: number | null | undefined;
  overagePolicy: TierOveragePolicy | null | undefined;
  overageCap: number | null | undefined;
}

export interface TierCapacityPolicyIssue {
  path: 'committed_seats' | 'overage_cap';
  message: string;
}

// The same rules the API applies to a tier's policy, so a form refuses what
// the server would: the pool fits the seat count, and billed overage is
// capped by the seats left above the pool.
export function getTierCapacityPolicyIssues({
  seatCount,
  committedSeats,
  overagePolicy,
  overageCap,
}: TierCapacityPolicy): TierCapacityPolicyIssue[] {
  const issues: TierCapacityPolicyIssue[] = [];
  const hasCommittedSeats = committedSeats != null;

  if (hasCommittedSeats && committedSeats > seatCount) {
    issues.push({
      path: 'committed_seats',
      message: `Pool size cannot exceed the ${seatCount} seats on this tier`,
    });
  }
  if (overagePolicy !== TierOveragePolicy.ALLOW_BILLED) {
    return issues;
  }
  if (!hasCommittedSeats) {
    issues.push({
      path: 'committed_seats',
      message: 'Set a pool size to bill overage',
    });
  }
  if (overageCap == null) {
    issues.push({
      path: 'overage_cap',
      message: 'Overage cap is required when overage is billed',
    });
    return issues;
  }
  if (hasCommittedSeats) {
    const seatsAboveCommitment = seatCount - committedSeats;
    if (overageCap > seatsAboveCommitment) {
      issues.push({
        path: 'overage_cap',
        message:
          seatsAboveCommitment > 0
            ? `Overage cap cannot exceed the ${seatsAboveCommitment} seats beyond the pool`
            : 'Make the pool smaller than the seat count to bill overage',
      });
    }
  }
  return issues;
}

export function addTierCapacityPolicyIssues(
  policy: TierCapacityPolicy,
  ctx: z.RefinementCtx
) {
  for (const issue of getTierCapacityPolicyIssues(policy)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: issue.message,
      path: [issue.path],
    });
  }
}
